import React, { lazy, Suspense, useCallback, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { CustomCSSProperties, TabsProps } from "./Tabs.type";
import TabsNavigation from "./TabsNavigation";
// @ts-ignore
import styles from "./Tabs.module.scss";

const Hero = lazy(() => import("@2u-landing-pages/templates/Hero").then((m) => ({ default: m.Hero })));
const Bento = lazy(() => import("@2u-landing-pages/templates/Bento").then((m) => ({ default: m.Bento })));
const Split = lazy(() => import("@2u-landing-pages/templates/Split").then((m) => ({ default: m.Split })));

const TAB_COMPONENTS: Record<string, React.ComponentType<any>> = {
  hero: Hero,
  bento: Bento,
  split: Split,
};

type PaddingMap = { [index: string]: string };

const paddingMap: PaddingMap = {
  "Large (64/32)": "large",
  "Medium (48/24)": "medium",
  "Small (32/16)": "small",
  "X-Small (24/12)": "xsmall",
};

const TabContent: React.FC<{
  tab: any;
  inspector: any;
  programLogo: any;
  isLoading: boolean;
  phoneNumber: string;
  tuitionCost: string;
}> = ({ tab, inspector, programLogo, isLoading, phoneNumber, tuitionCost }) => {
  if (!tab?.fields?.tab_content) return null;
  const { tab_content } = tab.fields;

  if (!tab_content?.sys?.contentType?.sys?.id) return null;
  const type = tab_content.sys.contentType.sys.id;
  const Component = TAB_COMPONENTS[type];

  return Component ? (
    <Component
      {...tab_content.fields}
      id={tab_content.sys.id}
      inspector={inspector}
      programLogo={programLogo}
      isLoading={isLoading}
      phoneNumber={phoneNumber}
      tuitionCost={tuitionCost}
    />
  ) : null;
};

export const Tabs: React.FC<TabsProps> = ({
  id,
  content,
  inspector,
  programLogo,
  isLoading,
  phoneNumber,
  tuitionCost,
  tabs_navigation_top_spacing = "Large (64/32)",
  tabs_navigation_bottom_spacing = "Large (64/32)",
  tabs_section_background,
  tab_button_size,
  tabs_border_color,
  button_style,
  animation = "fade",
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);

  // `light` has a dark grey background color but design wants to see a black background color
  const backgroundColor = button_style === "light" ? "dark-default" : `${button_style}-inverse`;
  // set up styles by checking if the fields are defined
  const customTabStyles: CustomCSSProperties = {};
  if (tabs_section_background) customTabStyles["--tabs-section-background"] = `var(${tabs_section_background})`;
  if (tabs_border_color) customTabStyles["--tabs-border-color"] = `var(${tabs_border_color})`;
  if (button_style) customTabStyles["--tabs-navigation-background"] = `var(--action-button-${backgroundColor})`;

  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLLIElement>, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      // find all siblings of the clicked tab
      const siblings = Array.from((e.currentTarget as HTMLElement).parentElement?.children || []);
      siblings.forEach((sibling) => sibling.classList.remove(styles.active));
      // add active class to the clicked tab
      (e.currentTarget as HTMLElement).classList.add(styles.active);

      if (sliderRef && index !== activeTab) {
        sliderRef.slickGoTo(index);
        setActiveTab(index);
      }
    },
    [activeTab, sliderRef]
  );

  const settings = {
    dots: false,
    infinite: true,
    speed: animation === "none" ? 0 : 250,
    slidesToShow: 1,
    slidesToScroll: 1,
    easing: "ease-in-out",
    fade: ["fade", "none"].includes(animation),
    beforeChange: (_: number, next: number) => setActiveTab(next),
    arrows: false,
    swipe: false,
  };

  if (!content?.length) return null;

  return (
    <section id={id} className={styles.tabs} style={customTabStyles}>
      <TabsNavigation
        content={content}
        pendingTab={activeTab}
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        button_style={button_style || "primary"}
        paddingTop={paddingMap[tabs_navigation_top_spacing]}
        paddingBottom={paddingMap[tabs_navigation_bottom_spacing]}
        tab_button_size={tab_button_size}
      />

      <div className={styles.tabPanel}>
        <Slider ref={setSliderRef} {...settings}>
          {content.map((tab, index) => (
            <div
              key={tab?.fields?.tab_label || index}
              role="tabpanel"
              id={`tabpanel-${index}`}
              className={styles.tabPanelItem}
              aria-labelledby={`tab-${index}`}
            >
              <Suspense fallback={<div className={styles.tabPanelLoading}>Loading...</div>}>
                <TabContent
                  tab={tab}
                  inspector={inspector}
                  programLogo={programLogo}
                  isLoading={isLoading}
                  phoneNumber={phoneNumber}
                  tuitionCost={tuitionCost}
                />
              </Suspense>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};
